import { useCallback, useRef } from 'react';
import { useForm as useFormBase } from 'react-hook-form';

import isArray from 'lodash/isArray';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import noop from 'lodash/noop';

import { yupResolver } from '@hookform/resolvers/yup';

import { is400 } from '@src/corelib/utils/httpUtils';
import { isPromise } from '@src/corelib/utils/validationUtils';

function getErrorResponse(error) {
  const responseData = error.response.data;
  return 'payload' in responseData ? responseData.payload : responseData;
}

function hasKey(key, schema) {
  if (schema.type === 'object') {
    return key in schema.fields;
  }

  if (schema.type === 'array') {
    if (schema.innerType.type === 'object') {
      return hasKey(key, schema.innerType);
    }
  }

  return false;
}

function useForm(params) {
  const { schema, defaultValues, showServerErrors = true, ...restParams } = params || {};

  const counter = useRef(null);

  const {
    setError,
    handleSubmit: handleSubmitBase,
    setValue,
    reset,
    ...form
  } = useFormBase({
    ...restParams,
    defaultValues,
    resolver: yupResolver(schema, { form: { defaultValues } }),
  });

  const setErrors = useCallback(
    (data, nonFieldError = () => {}) => {
      counter.current = 0;
      const parseErrors = (s, d, p = '') => {
        if (counter.current >= 10000) {
          throw new Error('Unable to parse object.');
        }
        counter.current += 1;

        for (const pair of Object.entries(d)) {
          const [key, value] = pair;
          const errorKey = p ? `${p}.${key}` : key;
          if (isString(value)) {
            if (hasKey(key, s)) {
              setError(errorKey, {
                type: 'invalid',
                message: value,
              });
            }
            else {
              nonFieldError({ key: errorKey, detail: value });
              break;
            }
          }
          else if (isArray(value)) {
            if (hasKey(key, s)) {
              value.forEach((item, index) => {
                if (isString(item)) {
                  setError(errorKey, {
                    type: 'invalid',
                    message: value,
                  });
                }
                else if (isObject(item)) {
                  const _errorKey = `${errorKey}[${index}]`;
                  parseErrors(s.fields[key], item, _errorKey);
                }
              });
            }
            else {
              nonFieldError({ key: errorKey, detail: value });
              break;
            }
          }
          else if (isObject(value)) {
            parseErrors(s.fields[key], value, errorKey);
          }
        }
      };

      try {
        parseErrors(schema, data);
      }
      catch (error) {
        console.error('Unable not parse errors.', error);
      }
    },
    [schema, setError],
  );

  const handleSubmit = useCallback(
    ({ onSubmit = noop, onSuccess = noop, onError = noop, onInvalid = noop }) => {
      return handleSubmitBase((values) => {
        const promise = onSubmit(values);
        if (isPromise(promise)) {
          return new Promise((resolve) => {
            promise
              .then((response) => {
                onSuccess(response);
                resolve();
              })
              .catch((error) => {
                if (is400(error?.response)) {
                  if (showServerErrors) {
                    setErrors(getErrorResponse(error));
                  }
                  onError(error, { isInvalid: true });
                }
                else {
                  console.error('Uexpected form error.', error);
                  onError(error, { isInvalid: false });
                }
                resolve();
              });
          });
        }

        console.log('Non Promise result. Form submitting attributes may not working.');
        return Promise.resolve();
      }, onInvalid);
    },
    [handleSubmitBase, setErrors, showServerErrors],
  );

  return {
    setError,
    setErrors,
    handleSubmitBase,
    handleSubmit,
    reset,
    setValue,
    ...form,
  };
}

export default useForm;
